"use client";

import { TutorialTooltip } from "@/components/common/tutorial-tooltip";
import { useComparisonStoreData } from "@/hooks/global-multiple";
import { trackEvent } from "@/utils/analytics/track-event";
import { ChevronUp } from "lucide-react";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Draggable, { DraggableData, DraggableEvent } from "react-draggable";
const STORAGE_KEY = "comparison_badge_position_v5"; // v5: Bottom-Left init
const TUTORIAL_ID = "comparison_badge_draggable";
export function DesktopComparisonBadge() {
  const comparisonTray = useComparisonStoreData(state => state.comparisonTray);
  const isComparisonMinimized = useComparisonStoreData(state => state.isComparisonMinimized);
  const setIsComparisonMinimized = useComparisonStoreData(state => state.setIsComparisonMinimized);
  const pathname = usePathname();

  // Draggable state
  const [position, setPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const hasDragged = useRef(false);
  const dragStartPosition = useRef({
    x: 0,
    y: 0
  });
  const nodeRef = useRef<HTMLDivElement>(null);
  const [bounds, setBounds] = useState({
    left: 0,
    top: 0,
    right: 0,
    bottom: 0
  });
  const [mounted, setMounted] = useState(false);

  // Show tutorial on first property addition
  const [showTutorial, setShowTutorial] = useState(false);
  const prevCountRef = useRef(0);
  const getViewportDimensions = () => ({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0
  });

  // Calculate simple viewport bounds from top-left (0,0)
  const calculateBounds = (rect?: DOMRect) => {
    if (typeof window === "undefined") return {
      left: 0,
      top: 0,
      right: 0,
      bottom: 0
    };
    const currentRect = rect || nodeRef.current?.getBoundingClientRect();
    // If using Portal/Fixed overlay, we can assume standard bounds
    // But we still need element size.
    const width = currentRect?.width || 200; // fallback width
    const height = currentRect?.height || 60; // fallback height

    const remInPixels = 16;
    const padding = 1 * remInPixels; // Reduced padding to 1rem to allow placing in corners
    const vp = getViewportDimensions();
    return {
      left: padding,
      top: padding,
      right: vp.width - width - padding,
      bottom: vp.height - height - padding
    };
  };

  // Initialize
  useEffect(() => {
    setMounted(true);
    if (typeof window === "undefined") return;
    const raf = requestAnimationFrame(() => {
      // Even with portal, we wait for mount to measure
      if (!nodeRef.current) {
        // Fallback to bottom-left area if ref missing
        const initX = 16;
        const initY = window.innerHeight - 80;
        setPosition({
          x: initX,
          y: initY
        });
        setBounds(calculateBounds());
        return;
      }
      const rect = nodeRef.current.getBoundingClientRect();
      const {
        width,
        height
      } = getViewportDimensions();

      // Initial Position Logic:
      // Try to find the map container to align relative to the map (ignoring side panel)
      // The user wants it to start where the floating controls were (bottom-left of MAP).
      const mapContainer = document.getElementById("map-container");
      let mapOffsetLeft = 0;
      if (mapContainer) {
        const mapRect = mapContainer.getBoundingClientRect();
        mapOffsetLeft = mapRect.left;
      }

      // bottom-8 left-4 relative to map
      // left: mapOffsetLeft + 16px
      // bottom: 32px (from viewport bottom) -> y = height - badgeHeight - 32

      const initX = mapOffsetLeft + 16;
      const initY = height - rect.height - 32;
      let initialPos = {
        x: initX,
        y: initY
      };

      // Try load saved
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed.x !== undefined && parsed.y !== undefined) {
            if (parsed.x !== 0 || parsed.y !== 0) {
              initialPos = parsed;
            }
          }
        } catch {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
      setPosition(initialPos);
      setBounds(calculateBounds(rect));
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resize handler
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => {
      setBounds(calculateBounds());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Tutorial logic
  useEffect(() => {
    const currentCount = comparisonTray?.length || 0;
    const previousCount = prevCountRef.current;
    if (currentCount === 1 && previousCount === 0) {
      setShowTutorial(true);
    }
    prevCountRef.current = currentCount;
  }, [comparisonTray]);
  const handleDragStart = useCallback((_e: DraggableEvent, data: DraggableData) => {
    setIsDragging(true);
    hasDragged.current = false;
    dragStartPosition.current = {
      x: data.x,
      y: data.y
    };
  }, []);
  const handleDrag = useCallback((_e: DraggableEvent, data: DraggableData) => {
    // Log removed, uncontrolled mode
    const deltaX = Math.abs(data.x - dragStartPosition.current.x);
    const deltaY = Math.abs(data.y - dragStartPosition.current.y);
    if (Math.sqrt(deltaX * deltaX + deltaY * deltaY) > 5) {
      hasDragged.current = true;
    }
  }, []);
  const handleDragStop = useCallback((_e: DraggableEvent, data: DraggableData) => {
    const newPosition = {
      x: data.x,
      y: data.y
    };
    // Uncontrolled: data.x/y is correct relative to offset parent (fixed overlay)
    console.log("[ComparisonBadge] Stop", newPosition);
    setPosition(newPosition); // Update state (won't trigger rerender of Draggable due to uncontrolled + key)
    setIsDragging(false);
    if (hasDragged.current) {
      if (typeof window !== "undefined") {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newPosition));
      }
      trackEvent({
        google: {
          event: "comparison_badge_repositioned",
          options: newPosition
        }
      });
    }
    setTimeout(() => hasDragged.current = false, 100);
  }, []);
  const handleClick = useCallback((e: React.MouseEvent) => {
    if (hasDragged.current || isDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    setIsComparisonMinimized(false);
    trackEvent({
      google: {
        event: "expand_comparison_from_badge",
        options: {
          property_count: comparisonTray?.length || 0
        }
      }
    });
  }, [isDragging, setIsComparisonMinimized, comparisonTray]);
  if (pathname === "/compare") return null;
  if (!isComparisonMinimized || !comparisonTray || comparisonTray.length === 0) return null;
  if (!mounted) return null;
  const draggableKey = position ? "draggable-ready" : "draggable-measuring";

  // Render via Portal to Body to ensure robust fixed positioning
  return createPortal(<div className="fixed inset-0 pointer-events-none z-[9999]" style={{
    zIndex: 9999
  }}>
			<Draggable key={draggableKey} nodeRef={nodeRef} defaultPosition={position || {
      x: 0,
      y: 0
    }} disabled={!position} onStart={handleDragStart} onDrag={handleDrag} onStop={handleDragStop} bounds={bounds}>
				<div ref={nodeRef} className="absolute inline-flex pointer-events-auto touch-none" style={{
        cursor: isDragging ? "grabbing" : "grab",
        opacity: position ? 1 : 0,
        visibility: position ? "visible" : "hidden"
        // No 'left/top' here, Draggable adds transform
      }}>
					{/* Tutorial Tooltip */}
					{showTutorial && <div className="relative">
							<TutorialTooltip tutorialId={TUTORIAL_ID} position="bottom" isOpen={showTutorial} onDismiss={() => setShowTutorial(false)}>
								<div className="space-y-2">
									<p className="font-semibold text-sm">Drag to Reposition</p>
									<p className="text-xs">You can drag this badge anywhere on the screen!</p>
								</div>
							</TutorialTooltip>
						</div>}

					<button onClick={handleClick} onPointerDown={e => e.stopPropagation()} className="bg-primary text-white shadow-lg hover:shadow-xl transition-all duration-300 group
							flex flex-row items-center gap-2 rounded-full px-4 py-3 hover:scale-110
							select-none cursor-pointer" aria-label="Expand comparison" style={{
          pointerEvents: "auto"
        }}>
						<ChevronUp className="w-4 h-4 group-hover:translate-y-[-2px] transition-transform" />
						<div className="flex flex-row items-center gap-2">
							<div className="relative">
								<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
								</svg>
								<span className="absolute -top-2 -right-2 bg-white text-primary text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 border-primary">
									{comparisonTray.length}
								</span>
							</div>
							<span className="font-semibold text-sm whitespace-nowrap">Compare</span>
						</div>
					</button>
				</div>
			</Draggable>
		</div>, document.body);
}